import { cn } from '@/utils/cn';
import { useTranslation } from 'react-i18next';
import { Minus, Plus } from 'lucide-react';

// ── Zoom levels ────────────────────────────────────────────────────────────────

export const ZOOM_LEVELS = [50, 75, 100, 125, 150, 200] as const;
export type ZoomLevel = typeof ZOOM_LEVELS[number];

interface ToothChartZoomProps {
  zoom: number;
  onChange: (n: number) => void;
  className?: string;
}

export function ToothChartZoom({ zoom, onChange, className }: ToothChartZoomProps) {
  const { t } = useTranslation();

  const snapToLevel = (n: number): number => {
    const sorted = [...ZOOM_LEVELS] as number[];
    return sorted.reduce((prev, curr) => (Math.abs(curr - n) < Math.abs(prev - n) ? curr : prev));
  };

  const handleDecrease = () => {
    const sorted = [...ZOOM_LEVELS] as number[];
    sorted.sort((a, b) => a - b);
    const current = snapToLevel(zoom);
    const idx = sorted.indexOf(current);
    if (idx > 0) onChange(sorted[idx - 1]);
  };

  const handleIncrease = () => {
    const sorted = [...ZOOM_LEVELS] as number[];
    sorted.sort((a, b) => a - b);
    const current = snapToLevel(zoom);
    const idx = sorted.indexOf(current);
    if (idx < sorted.length - 1) onChange(sorted[idx + 1]);
  };

  return (
    <div
      className={cn(
        'inline-flex items-center rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm overflow-hidden',
        className,
      )}
      aria-label={t('dental.chart.zoom.label', 'Zoom')}
    >
      <button
        type="button"
        onClick={handleDecrease}
        disabled={zoom <= ZOOM_LEVELS[0]}
        title={t('dental.chart.zoom.out', 'Zoom ud')}
        className="px-2.5 py-1.5 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
      >
        <Minus className="h-3.5 w-3.5" />
      </button>

      {/* Dropdown / current level */}
      <select
        value={zoom}
        onChange={(e) => onChange(Number(e.target.value))}
        className="px-1 py-1.5 text-xs font-semibold text-slate-700 dark:text-slate-200 bg-transparent border-x border-slate-200 dark:border-slate-700 focus:outline-none cursor-pointer"
        aria-label={t('dental.chart.zoom.label', 'Zoom')}
      >
        {ZOOM_LEVELS.map((level) => (
          <option key={level} value={level}>
            {level}%
          </option>
        ))}
      </select>

      <button
        type="button"
        onClick={handleIncrease}
        disabled={zoom >= ZOOM_LEVELS[ZOOM_LEVELS.length - 1]}
        title={t('dental.chart.zoom.in', 'Zoom ind')}
        className="px-2.5 py-1.5 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
      >
        <Plus className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
